import { useState, useCallback } from 'react';
import { MediaPickerModal } from './MediaLibrary/MediaPickerModal';
import { Button } from './ui/Button';
import { FormSection } from './ui/FormSection';
import { trpc } from '../lib/trpc';
import type { Media } from '@wineryxplorer/shared';
import { ImgWithSkeleton } from './ui/ImgWithSkeleton';

export interface TastingExperienceMediaUploadProps {
  venueId: string;
  heroImageId?: string;
  imageIds: string[];
  videoIds: string[];
  onHeroImageChange: (heroImageId: string) => void;
  onImagesChange: (imageIds: string[]) => void;
  onVideosChange: (videoIds: string[]) => void;
  maxImages?: number;
  maxVideos?: number;
  className?: string;
}

export const TastingExperienceMediaUpload = ({
  venueId,
  heroImageId,
  imageIds,
  videoIds,
  onHeroImageChange,
  onImagesChange,
  onVideosChange,
  maxImages = 10,
  maxVideos = 5,
  className = '',
}: TastingExperienceMediaUploadProps) => {
  const [showHeroModal, setShowHeroModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Fetch media to display thumbnails
  const allMediaIds = [
    ...(heroImageId ? [heroImageId] : []),
    ...imageIds,
    ...videoIds,
  ];
  
  const { data: mediaList } = trpc.media.getByIds.useQuery(
    { ids: allMediaIds },
    { enabled: allMediaIds.length > 0 }
  );

  const media = (mediaList ?? []) as Media[];

  const handleRemoveImage = useCallback((imageId: string) => {
    onImagesChange(imageIds.filter(id => id !== imageId));
  }, [imageIds, onImagesChange]);

  const handleRemoveVideo = useCallback((videoId: string) => {
    onVideosChange(videoIds.filter(id => id !== videoId));
  }, [videoIds, onVideosChange]);

  const handleHeroImageSelect = useCallback((ids: string[]) => {
    if (ids.length > 0) {
      onHeroImageChange(ids[0]);
    }
  }, [onHeroImageChange]);

  const handleRemoveHeroImage = useCallback(() => {
    onHeroImageChange('');
  }, [onHeroImageChange]);

  return (
    <div className={className}>
      <FormSection
        title="Hero Image"
        titleAs="h3"
        // The wrapper div hides these sections from the form's `:first-child`
        // rule, and media never leads a form — so the separator is explicit.
        divider
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHeroModal(true)}
          >
            {heroImageId ? 'Change Hero Image' : 'Add Hero Image'}
          </Button>
        }
      >
        {/* Hero Image Display */}
        {heroImageId ? (
          <div className="relative group">
            <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
              {(() => {
                const mediaItem = media.find(m => m.id === heroImageId);
                return mediaItem?.cloudinaryUrl ? (
                  <ImgWithSkeleton
                    src={mediaItem.cloudinaryUrl}
                    alt={mediaItem.caption || 'Hero image'}
                    className="w-full h-full object-cover"
                    skeletonClassName="rounded-lg"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                );
              })()}
            </div>
            
            {/* Remove button */}
            <button
              type="button"
              onClick={handleRemoveHeroImage}
              className="absolute top-2 right-2 w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remove hero image"
            >
              ×
            </button>
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-gray-600">No hero image added yet</p>
            <p className="text-xs text-gray-500 mt-1">Click "Add Hero Image" to select an image</p>
          </div>
        )}
      </FormSection>

      <FormSection
        title="Image Gallery"
        titleAs="h3"
        description={maxImages ? `${imageIds.length} of ${maxImages} images used.` : undefined}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowImageModal(true)}
            disabled={maxImages ? imageIds.length >= maxImages : false}
          >
            {imageIds.length > 0 ? 'Manage Images' : 'Add Images'}
          </Button>
        }
      >
        {/* Image Thumbnails */}
        {imageIds.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {imageIds.map((imageId, index) => {
              const mediaItem = media.find(m => m.id === imageId);
              return (
                <div key={imageId} className="relative group">
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                    {mediaItem?.cloudinaryUrl ? (
                      <ImgWithSkeleton
                        src={mediaItem.cloudinaryUrl}
                        alt={mediaItem.caption || 'Gallery image'}
                        className="w-full h-full object-cover"
                        skeletonClassName="rounded-lg"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Order indicator */}
                  <div className="absolute top-2 left-2 w-6 h-6 bg-primary-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {index + 1}
                  </div>
                  
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveImage(imageId)}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove image"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-sm text-gray-600">No images added yet</p>
            <p className="text-xs text-gray-500 mt-1">Click "Add Images" to select images</p>
          </div>
        )}
      </FormSection>

      <FormSection
        title="Videos"
        titleAs="h3"
        description={maxVideos ? `${videoIds.length} of ${maxVideos} videos used.` : undefined}
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowVideoModal(true)}
            disabled={maxVideos ? videoIds.length >= maxVideos : false}
          >
            {videoIds.length > 0 ? 'Manage Videos' : 'Add Videos'}
          </Button>
        }
      >
        {/* Video Thumbnails */}
        {videoIds.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {videoIds.map((videoId, index) => {
              const mediaItem = media.find(m => m.id === videoId);
              return (
                <div key={videoId} className="relative group">
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                    {mediaItem?.cloudinaryUrl ? (
                      <video
                        src={mediaItem.cloudinaryUrl}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Order indicator */}
                  <div className="absolute top-2 left-2 w-6 h-6 bg-primary-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {index + 1}
                  </div>
                  
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => handleRemoveVideo(videoId)}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove video"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-gray-600">No videos added yet</p>
            <p className="text-xs text-gray-500 mt-1">Click "Add Videos" to select videos</p>
          </div>
        )}
      </FormSection>

      {/* Hero Image Picker Modal */}
      <MediaPickerModal
        isOpen={showHeroModal}
        onClose={() => setShowHeroModal(false)}
        mode="single"
        venueId={venueId}
        selectedIds={heroImageId ? [heroImageId] : []}
        onSelectionChange={handleHeroImageSelect}
        title="Select Hero Image"
        showUpload={true}
      />

      {/* Image Gallery Picker Modal */}
      <MediaPickerModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        mode="multi-ordered"
        venueId={venueId}
        selectedIds={imageIds}
        onSelectionChange={onImagesChange}
        title="Select Gallery Images"
        maxSelection={maxImages}
        showUpload={true}
      />

      {/* Video Picker Modal */}
      <MediaPickerModal
        isOpen={showVideoModal}
        onClose={() => setShowVideoModal(false)}
        mode="multi-ordered"
        venueId={venueId}
        selectedIds={videoIds}
        onSelectionChange={onVideosChange}
        title="Select Videos"
        maxSelection={maxVideos}
        showUpload={true}
      />
    </div>
  );
};
